import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import PermissionGate from '../../../components/PermissionGate';
import { formatCurrency, formatDate } from '../../regureStyles';
import { useLocale } from '../../../utils/LocaleContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const FinancialsTab = ({ claim, refreshClaim, S }) => {
  const { t } = useTranslation();
  useLocale(); // reactive — re-renders when locale changes
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payRecipient, setPayRecipient] = useState('');
  const [payMethod, setPayMethod] = useState('bank_transfer');
  const [payNotes, setPayNotes] = useState('');
  const [paying, setPaying] = useState(false);
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveAmount, setReserveAmount] = useState(claim?.reserve || '');
  const [reserveReason, setReserveReason] = useState('');
  const [settingReserve, setSettingReserve] = useState(false);

  const handleRecordPayment = async () => {
    if (!payAmount || isNaN(Number(payAmount)) || Number(payAmount) <= 0) {
      toast.error(t('screens.claimsScreen.detail.paymentAmountInvalid'));
      return;
    }
    setPaying(true);
    try {
      await axios.post(
        `${BASE_URL}/claims/${claim._id}/payment`,
        { amount: Number(payAmount), recipient: payRecipient, method: payMethod, notes: payNotes },
        { withCredentials: true },
      );
      toast.success(t('screens.claimsScreen.detail.paymentRecorded'));
      setShowPayModal(false);
      setPayAmount('');
      setPayRecipient('');
      setPayMethod('bank_transfer');
      setPayNotes('');
      refreshClaim?.();
    } catch (err) {
      toast.error(err.response?.data?.message || t('screens.claimsScreen.detail.paymentFailed'));
    } finally {
      setPaying(false);
    }
  };

  const handleSetReserve = async () => {
    if (!reserveAmount || isNaN(Number(reserveAmount)) || Number(reserveAmount) < 0) {
      toast.error(t('screens.claimsScreen.detail.reserveInvalid'));
      return;
    }
    setSettingReserve(true);
    try {
      await axios.patch(`${BASE_URL}/claims/${claim._id}/reserve`, { amount: Number(reserveAmount), reason: reserveReason }, { withCredentials: true });
      toast.success(t('screens.claimsScreen.detail.reserveUpdated'));
      setShowReserveModal(false);
      setReserveAmount('');
      setReserveReason('');
      refreshClaim?.();
    } catch (err) {
      toast.error(err.response?.data?.message || t('screens.claimsScreen.detail.reserveFailed'));
    } finally {
      setSettingReserve(false);
    }
  };

  const paid = claim.paidAmount || 0;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 28 }}>
        {[
          { label: t('screens.claimsScreen.detail.reserveTitle'), value: formatCurrency(claim.reserve), sub: t('screens.claimsScreen.detail.reserveSub'), color: S.burgundy },
          { label: t('screens.claimsScreen.detail.totalPaid'), value: formatCurrency(paid), sub: paid > 0 ? t('screens.claimsScreen.detail.paymentCount', { count: (claim.payments || []).length }) : t('screens.claimsScreen.detail.noPayments'), color: '#10b981' },
          { label: t('screens.claimsScreen.detail.totalIncurred'), value: formatCurrency(claim.reserve + paid), sub: t('screens.claimsScreen.detail.reservePlusPaid'), color: '#3b82f6' },
        ].map(card => (
          <div key={card.label} style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>{card.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>{card.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.claimsScreen.detail.reserveHistory')}</div>
          <PermissionGate resource='claims' action='reserve'>
            <button
              onClick={() => setShowReserveModal(true)}
              style={{
                padding: '8px 16px',
                background: '#f0f4f8',
                color: S.textPrimary,
                border: `1px solid ${S.borderLight}`,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('screens.claimsScreen.detail.setReserve')}
            </button>
          </PermissionGate>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${S.borderLight}` }}>
              {[t('screens.claimsScreen.detail.colDate'), t('screens.claimsScreen.detail.colPrevious'), t('screens.claimsScreen.detail.colNewAmount'), t('screens.claimsScreen.detail.colChangedBy'), t('screens.claimsScreen.detail.colReason')].map(h => (
                <th
                  key={h}
                  style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(claim?.reserveHistory || []).map((r, i) => (
              <tr key={r._id || r.id || i} style={{ borderBottom: `1px solid ${S.borderLight}` }}>
                <td style={{ padding: '12px', fontSize: 13, color: S.textMuted }}>
                  {r.changedAt ? formatDate(r.changedAt) : formatDate(r.timestamp)}
                </td>
                <td style={{ padding: '12px', fontSize: 13, color: S.textSecondary }}>{formatCurrency(r.oldAmount)}</td>
                <td style={{ padding: '12px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{formatCurrency(r.newAmount)}</td>
                <td style={{ padding: '12px', fontSize: 13, color: S.textSecondary }}>{r.changedByName || r.changedBy}</td>
                <td style={{ padding: '12px', fontSize: 13, color: S.textSecondary }}>{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.claimsScreen.detail.payments')}</div>
          <PermissionGate resource='claims' action='payment'>
            <button
              onClick={() => setShowPayModal(true)}
              style={{
                padding: '8px 16px',
                background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('screens.claimsScreen.detail.recordPayment')}
            </button>
          </PermissionGate>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${S.borderLight}` }}>
              {[t('screens.claimsScreen.detail.colDate'), t('screens.claimsScreen.detail.colAmount'), t('screens.claimsScreen.detail.colRecipient'), t('screens.claimsScreen.detail.colMethod'), t('screens.claimsScreen.detail.colPaymentStatus')].map(h => (
                <th
                  key={h}
                  style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(claim?.payments || []).map((p, i) => (
              <tr key={p._id || p.id || i} style={{ borderBottom: `1px solid ${S.borderLight}` }}>
                <td style={{ padding: '12px', fontSize: 13, color: S.textMuted }}>{p.date ? formatDate(p.date) : '-'}</td>
                <td style={{ padding: '12px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{formatCurrency(p.amount)}</td>
                <td style={{ padding: '12px', fontSize: 13, color: S.textSecondary }}>{p.recipient || '—'}</td>
                <td style={{ padding: '12px', fontSize: 13, color: S.textSecondary }}>{p.method || '—'}</td>
                <td style={{ padding: '12px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#dcfce7', color: '#15803d' }}>
                    {p.status || t('screens.claimsScreen.detail.paymentProcessed')}
                  </span>
                </td>
              </tr>
            ))}
            {(claim?.payments || []).length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '20px 12px', textAlign: 'center', fontSize: 13, color: S.textMuted }}>
                  {t('screens.claimsScreen.detail.noPaymentsRecorded')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPayModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 440, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 24 }}>{t('screens.claimsScreen.detail.recordPayment')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, display: 'block', marginBottom: 6 }}>{t('screens.claimsScreen.detail.paymentAmountRequired')}</label>
                <input
                  type='number'
                  min='0'
                  step='0.01'
                  value={payAmount}
                  onChange={e => setPayAmount(e.target.value)}
                  placeholder='0.00'
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, display: 'block', marginBottom: 6 }}>{t('screens.claimsScreen.detail.recipient')}</label>
                <input
                  type='text'
                  value={payRecipient}
                  onChange={e => setPayRecipient(e.target.value)}
                  placeholder={t('screens.claimsScreen.detail.payeeName')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, display: 'block', marginBottom: 6 }}>{t('screens.claimsScreen.detail.paymentMethod')}</label>
                <select
                  value={payMethod}
                  onChange={e => setPayMethod(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    background: '#fff',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value='bank_transfer'>{t('screens.claimsScreen.detail.methodBank')}</option>
                  <option value='cheque'>{t('screens.claimsScreen.detail.methodCheque')}</option>
                  <option value='cash'>{t('screens.claimsScreen.detail.methodCash')}</option>
                  <option value='digital_wallet'>{t('screens.claimsScreen.detail.methodDigitalWallet')}</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, display: 'block', marginBottom: 6 }}>{t('screens.claimsScreen.detail.notes')}</label>
                <textarea
                  value={payNotes}
                  onChange={e => setPayNotes(e.target.value)}
                  placeholder={t('screens.claimsScreen.detail.optionalNotes')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: 72,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                onClick={() => setShowPayModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  color: S.textSecondary,
                  cursor: 'pointer',
                }}
              >
                {t('screens.claimsScreen.detail.cancel')}
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={paying}
                style={{
                  padding: '10px 20px',
                  background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: paying ? 'not-allowed' : 'pointer',
                  opacity: paying ? 0.7 : 1,
                }}
              >
                {paying ? t('screens.claimsScreen.detail.saving') : t('screens.claimsScreen.detail.recordPayment')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReserveModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 440, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 24 }}>{t('screens.claimsScreen.detail.setReserveTitle')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, display: 'block', marginBottom: 6 }}>{t('screens.claimsScreen.detail.reserveAmount')}</label>
                <input
                  type='number'
                  value={reserveAmount}
                  onChange={e => setReserveAmount(e.target.value)}
                  placeholder={t('screens.claimsScreen.detail.reservePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, display: 'block', marginBottom: 6 }}>{t('screens.claimsScreen.detail.reasonForChange')}</label>
                <textarea
                  value={reserveReason}
                  onChange={e => setReserveReason(e.target.value)}
                  placeholder={t('screens.claimsScreen.detail.optionalReason')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: 72,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                onClick={() => setShowReserveModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  color: S.textSecondary,
                  cursor: 'pointer',
                }}
              >
                {t('screens.claimsScreen.detail.cancel')}
              </button>
              <button
                onClick={handleSetReserve}
                disabled={settingReserve}
                style={{
                  padding: '10px 20px',
                  background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: settingReserve ? 'not-allowed' : 'pointer',
                  opacity: settingReserve ? 0.7 : 1,
                }}
              >
                {settingReserve ? t('screens.claimsScreen.detail.saving') : t('screens.claimsScreen.detail.setReserve')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialsTab;
